import React from "react";
import { Link } from "react-router-dom";

type LegalPageKind = "privacy" | "eula";

const updatedDate = "May 14, 2026";

export function PrivacyPolicy() {
  return <LegalPage kind="privacy" />;
}

export function EndUserLicenseAgreement() {
  return <LegalPage kind="eula" />;
}

function LegalPage({ kind }: { kind: LegalPageKind }) {
  const isPrivacy = kind === "privacy";
  const title = isPrivacy ? "Privacy Policy" : "End User License Agreement";

  return (
    <main style={pageStyle}>
      <section style={shellStyle}>
        <Link to="/" style={brandStyle}>
          LENSES
        </Link>
        <h1 style={titleStyle}>{title}</h1>
        <p style={metaStyle}>Last updated: {updatedDate}</p>
        {isPrivacy ? <PrivacyContent /> : <EulaContent />}
      </section>
    </main>
  );
}

function PrivacyContent() {
  return (
    <div style={contentStyle}>
      <p>
        Lenses CPM helps organizations organize strategy, KPIs, owners,
        initiatives, evidence, feedback, and reporting. This Privacy Policy
        explains how Lenses collects, uses, and protects information when you
        use the service.
      </p>

      <h2>Information We Collect</h2>
      <p>
        We collect account information such as name, email address, role, and
        organization assignments. We also store workspace data entered into
        Lenses, including strategy maps, KPI definitions, KPI values, targets,
        owners, initiatives, tasks, poll settings, poll responses, and report
        settings.
      </p>
      <p>
        If you connect QuickBooks Online, Lenses receives authorization tokens
        from Intuit and may retrieve accounting data such as account names,
        account identifiers, report totals, date ranges, and related metadata
        needed to preview and import financial metrics into KPI history.
      </p>

      <h2>How We Use Information</h2>
      <p>
        We use information to provide the Lenses product, authenticate users,
        manage permissions, save workspace changes, generate reports, send
        requested poll communications, summarize feedback, and preview or import
        authorized QuickBooks data into KPIs.
      </p>
      <p>
        QuickBooks data is used only to provide integration features requested
        by authorized users, such as previewing giving or income totals and
        applying those totals to KPI actual history.
      </p>

      <h2>Sharing</h2>
      <p>
        We do not sell customer data or QuickBooks data. We may share data with
        service providers that help operate Lenses, such as hosting, email
        delivery, authentication, analytics, and AI-assisted summarization,
        when needed to provide the service.
      </p>

      <h2>QuickBooks Connection And Disconnection</h2>
      <p>
        Lenses connects to QuickBooks only after an authorized user completes
        Intuit's authorization flow. Users with appropriate permissions can
        disconnect QuickBooks from within Lenses, and access can also be revoked
        through Intuit or QuickBooks app management.
      </p>

      <h2>Security</h2>
      <p>
        We use reasonable administrative, technical, and organizational measures
        to protect information. QuickBooks authorization tokens are stored on
        the server and are not exposed to the browser as plain application data.
      </p>

      <h2>Data Retention</h2>
      <p>
        We retain account, workspace, KPI, integration, and communication data
        as long as needed to provide the service, comply with legal obligations,
        resolve disputes, or enforce agreements.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about this Privacy Policy can be sent to the Lenses
        administrator or support contact for your organization.
      </p>
    </div>
  );
}

function EulaContent() {
  return (
    <div style={contentStyle}>
      <p>
        This End User License Agreement governs your use of Lenses CPM. By using
        Lenses, you agree to use the service only for authorized organizational
        strategy, KPI, reporting, feedback, and integration workflows.
      </p>

      <h2>License</h2>
      <p>
        Subject to this agreement and any applicable subscription or access
        terms, Lenses grants authorized users a limited, non-exclusive,
        non-transferable right to access and use the service for internal
        organizational purposes.
      </p>

      <h2>Authorized Use</h2>
      <p>
        You are responsible for using Lenses only with data you are authorized
        to view, enter, edit, import, or share. You must keep sign-in
        credentials secure and must not attempt to bypass permissions,
        disrupt the service, reverse engineer the application, or use the
        service for unlawful purposes.
      </p>

      <h2>Customer Data</h2>
      <p>
        Your organization retains ownership of the data it enters into Lenses.
        Lenses may process that data to provide application functionality,
        including strategy maps, KPI dashboards, reports, polls, email
        workflows, AI-assisted summaries, and integrations.
      </p>

      <h2>QuickBooks Integration</h2>
      <p>
        If you connect QuickBooks Online, you authorize Lenses to access
        QuickBooks data permitted by the Intuit authorization flow. Lenses uses
        this data to provide integration features such as account lookup,
        financial previews, and importing selected totals into KPI actual
        history. You may disconnect QuickBooks access through Lenses or through
        Intuit's app management tools.
      </p>

      <h2>Third-Party Services</h2>
      <p>
        Lenses may integrate with third-party services such as Intuit
        QuickBooks, email providers, hosting providers, and AI services. Your
        use of those services may also be governed by their terms and privacy
        policies.
      </p>

      <h2>No Warranty</h2>
      <p>
        Lenses is provided on an as-available basis. While the service is
        designed to support organizational clarity and reporting, users are
        responsible for reviewing imported, calculated, and summarized data
        before relying on it for financial, operational, or leadership
        decisions.
      </p>

      <h2>Limitation Of Liability</h2>
      <p>
        To the maximum extent permitted by law, Lenses is not liable for
        indirect, incidental, consequential, special, or punitive damages, or
        for loss of profits, revenue, data, or business opportunities arising
        from use of the service.
      </p>

      <h2>Changes</h2>
      <p>
        Lenses may update this agreement from time to time. Continued use of
        the service after updates means you accept the revised terms.
      </p>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "#f8fafc",
  color: "#111827",
  fontFamily:
    "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  padding: "48px 18px",
};

const shellStyle: React.CSSProperties = {
  maxWidth: 920,
  margin: "0 auto",
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: "34px clamp(22px, 5vw, 54px)",
  boxShadow: "0 18px 42px rgba(15, 23, 42, 0.08)",
};

const brandStyle: React.CSSProperties = {
  display: "inline-block",
  color: "#16a34a",
  textDecoration: "none",
  fontWeight: 900,
  letterSpacing: 1,
  fontSize: 13,
  marginBottom: 20,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 40,
  lineHeight: 1.08,
  color: "#0f172a",
};

const metaStyle: React.CSSProperties = {
  margin: "10px 0 26px",
  color: "#64748b",
  fontSize: 14,
  fontWeight: 700,
};

const contentStyle: React.CSSProperties = {
  display: "grid",
  gap: 14,
  fontSize: 16,
  lineHeight: 1.68,
  color: "#334155",
};

